import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

const GaugeLoadingState = ({
  animate = true,
  height: _height = 400,
  style = {},
  className = ''
}) => {
  const loadingContainerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: '100%', _height })

  useLayoutEffect(() => {
    if (loadingContainerRef.current) {
      const { clientWidth, clientHeight } = loadingContainerRef.current
      setDimensions({
        width: clientWidth || 800,
        height: clientHeight || 400
      })
    }
  }, [])

  const gaugeConfig = useMemo(() => {
    const { width, height } = dimensions
    const strokeWidth = 24
    const centerX = width / 2
    const centerY = height - 24
    const radius = Math.min(width / 2, centerY) - strokeWidth / 2
    const startX = centerX - radius
    const endX = centerX + radius

    return {
      strokeWidth,
      centerX,
      centerY,
      radius,
      startX,
      endX,
      arcPath: `M ${startX},${centerY} A ${radius},${radius} 0 0 1 ${endX},${centerY}`,
      animationDuration: '1.8s',
      keyTimes: '0;0.5;1'
    }
  }, [dimensions])

  const animations = useMemo(() => {
    const { strokeWidth } = gaugeConfig
    return {
      strokeWidthValues: `${strokeWidth * 0.65};${strokeWidth};${strokeWidth * 0.65}`,
      circleRadius: strokeWidth / 2
    }
  }, [gaugeConfig])

  const { width, height } = dimensions
  const {
    strokeWidth,
    arcPath,
    animationDuration,
    keyTimes
  } = gaugeConfig

  return (
    <div style={{ position: 'relative', ...style, height: _height }} className={className}>
      <div
        ref={loadingContainerRef}
        style={{
          position: 'absolute',
          inset: 0
        }}
      >
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
        >
          <defs>
            <path id='arcPath' d={arcPath} />
          </defs>

          <path
            d={arcPath}
            fill='none'
            stroke='var(--color-neutral-200)'
            strokeWidth={strokeWidth}
            strokeLinecap='round'
          >
            {animate && (
              <animate
                attributeName='stroke-width'
                values={animations.strokeWidthValues}
                dur={animationDuration}
                repeatCount='indefinite'
                keyTimes={keyTimes}
                calcMode='spline'
                keySplines='0.42 0 0.58 1; 0.42 0 0.58 1'
              />
            )}
          </path>

          {animate && (
            <circle
              r={animations.circleRadius}
              fill='var(--color-neutral-400)'
            >
              <animateMotion
                dur={animationDuration}
                repeatCount='indefinite'
                rotate='auto'
                keyPoints='0;1;0'
                keyTimes={keyTimes}
                calcMode='linear'
              >
                <mpath href='#arcPath' />
              </animateMotion>
            </circle>
          )}
        </svg>
      </div>
    </div>
  )
}

GaugeLoadingState.propTypes = {
  animate: PropTypes.bool,
  height: PropTypes.number,
  style: PropTypes.object,
  className: PropTypes.string
}

export default GaugeLoadingState
